import React, { useEffect, useRef, useState } from "react";
import { supabase } from "./lib/supabaseClient";

export default function Dashboard() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false); // background refresh flag to avoid table flash
  const [cleanedBooks, setCleanedBooks] = useState(new Set()); // UI copy
  const cleanedBooksRef = useRef(new Set()); // Source of truth used inside polling closures

  useEffect(() => {
    // Add noindex meta tag for SEO
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);
    
    fetchBooks({ initial: true });
    // Set up polling to check for status updates
    const interval = setInterval(() => fetchBooks({ initial: false }), 10000); // background refresh
    
    return () => {
      clearInterval(interval);
      // Clean up meta tag when component unmounts
      const existingTag = document.head.querySelector('meta[name="robots"]');
      if (existingTag) {
        document.head.removeChild(existingTag);
      }
    };
  }, []);

  async function fetchBooks({ initial = false } = {}) {
    if (initial) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    const { data, error } = await supabase.from("books").select("*").order("created_at", { ascending: false });
    if (!error) {
      setBooks(data || []);
      console.log('Fetched books:', data);
      
      // Check for books that have been stuck in processing for too long (more than 10 minutes)
      const now = new Date();
      const stuckBooks = data?.filter(b => {
        if (b.status === 'parsing' || b.status === 'processing') {
          const createdTime = new Date(b.created_at);
          const timeDiff = now - createdTime;
          const minutesStuck = timeDiff / (1000 * 60);
          // Only clean books we haven't cleaned before (use ref to avoid stale closures)
          return minutesStuck > 10 && !cleanedBooksRef.current.has(b.id);
        }
        return false;
      }) || [];
      
      if (stuckBooks.length > 0) {
        console.log('Found books stuck in parsing status for too long:', stuckBooks.map(b => ({ 
          id: b.id, 
          title: b.title, 
          status: b.status,
          created_at: b.created_at,
          minutes_stuck: Math.round((now - new Date(b.created_at)) / (1000 * 60))
        })));
        
        // Auto-heal: if chapters exist for a stuck book, promote to ready
        console.log('Auto-heal check for stuck books (promote to ready if chapters exist)...');
        const cleanedIds = new Set();
        for (const book of stuckBooks) {
          try {
            // Check chapter presence fast (head:true)
            const { count, error: chaptersErr } = await supabase
              .from('chapters')
              .select('id', { count: 'exact', head: true })
              .eq('book_id', book.id);
            if (!chaptersErr && typeof count === 'number' && count > 0) {
              const { error: promoteErr, status: promoteStatus } = await supabase
                .from('books')
                .update({ status: 'ready' })
                .eq('id', book.id);
              if (!promoteErr) {
                console.log(`Auto-heal: promoted book ${book.id} to ready (chapters=${count}) [status=${promoteStatus}]`);
                cleanedIds.add(book.id);
                continue;
              }
              console.warn('Auto-heal promote failed; will fall back to error:', { bookId: book.id, promoteStatus, promoteErr });
            }
            // If not healable and older than 10 minutes, mark as error
            const minutesStuck = Math.round((now - new Date(book.created_at)) / (1000 * 60));
            if (minutesStuck > 10) {
              const { error, status: errStatus } = await supabase
                .from("books")
                .update({ status: 'error' })
                .eq('id', book.id);
              if (!error) {
                console.log(`Successfully marked stuck book ${book.id} as error [status=${errStatus}]`);
                cleanedIds.add(book.id);
              } else {
                console.error(`Failed to cleanup stuck book ${book.id}:`, error);
              }
            }
          } catch (cleanupError) {
            console.error(`Exception cleaning up stuck book ${book.id}:`, cleanupError);
          }
        }
        
        // Update our cleaned books set
        if (cleanedIds.size > 0) {
          setCleanedBooks(prev => {
            const next = new Set([...prev, ...cleanedIds]);
            cleanedBooksRef.current = next;
            return next;
          });
          console.log(`Added ${cleanedIds.size} books to cleaned set`);
        }
      }
    }
    if (initial) {
      setLoading(false);
    } else {
      setIsRefreshing(false);
    }
  }

  function handleBookClick(book) {
    // Navigate to book editor for any book (regardless of status)
    window.location.href = `/editor/${book.id}`;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff' }}>
      {/* Header - App page header (simplified, truly left-aligned logo) */}
      <div style={{ 
        width: '100%',
        padding: '2rem 0 1.5rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem'
        }}>
          <div className="logo" style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/'}>
            <img alt="Light Reads logo" className="logo-img" src="/logo-quote.png" />
            <span className="logo-text">Light Reads</span>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <main style={{ flex: 1, width: '100%', maxWidth: 900, margin: '0 auto', padding: '0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '32px 0 24px 0' }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            Books
            {isRefreshing && (
              <span className="spinner" title="Refreshing" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #ddd', borderTop: '2px solid #999', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
            )}
          </h2>
          <button style={{ fontWeight: 500, fontSize: 16, padding: '10px 20px', borderRadius: 8, border: '1px solid #bbb', background: '#fff', cursor: 'pointer' }}>
            + Create New Audio Book
          </button>
        </div>
        <input placeholder="Search books..." style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #bbb', marginBottom: 24, fontSize: 15 }} />
        {loading ? <div>Loading...</div> : (
          <div>
            {books.map((book, index) => (
              <div key={book.id} style={{ display: 'flex', alignItems: 'center', padding: 16, borderBottom: '1px solid #eee', opacity: book.status === 'ready' ? 1 : 0.7, cursor: 'pointer', flexWrap: 'wrap' }} onClick={() => handleBookClick(book)}>
                <div style={{ fontSize: 28, marginRight: 16, minWidth: 32 }}>📄</div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontWeight: 500, fontSize: 16, wordBreak: 'break-all', color: '#333', cursor: 'pointer' }}>{book.title}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{new Date(book.created_at).toLocaleString()}</div>
                </div>
                <div style={{ minWidth: 200, textAlign: 'right', marginTop: 8, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  {/* Only show status for the first book (most recent) */}
                  {index === 0 && (book.status === 'parsing' || book.status === 'processing') ? (
                    (() => {
                      const now = new Date();
                      const createdTime = new Date(book.created_at);
                      const timeDiff = now - createdTime;
                      const minutesStuck = timeDiff / (1000 * 60);
                      
                      if (minutesStuck > 10) {
                        return <span style={{ color: '#f44336', fontSize: 12 }}>Stuck in processing. Please delete and re-upload.</span>;
                      } else {
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="spinner" style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #bbb', borderTop: '2px solid #888', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                            <span style={{ fontSize: 12, color: '#666', maxWidth: 150, textAlign: 'right' }}>
                              Processing... ({Math.round(minutesStuck)}m)
                            </span>
                          </div>
                        );
                      }
                    })()
                  ) : index === 0 && book.status === 'error' ? (
                    <span style={{ color: '#f44336', fontSize: 12 }}>Processing failed</span>
                  ) : book.status === 'ready' ? (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookClick(book);
                      }}
                      style={{
                        background: '#4caf50',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#45a049'}
                      onMouseOut={(e) => e.target.style.background = '#4caf50'}
                    >
                      START EDITING
                    </button>
                  ) : index === 0 ? (
                    <span style={{ color: '#888', fontSize: 12 }}>Unknown status: {book.status}</span>
                  ) : null}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete book \"${book.title}\"? This cannot be undone.`)) {
                        console.log('🗑️ [DASHBOARD] Deleting book:', book.id);
                        const { error } = await supabase.from("books").delete().eq("id", book.id);
                        if (error) {
                          console.error('❌ [DASHBOARD] Delete failed:', error);
                          alert('Failed to delete book: ' + error.message);
                        } else {
                          console.log('✅ [DASHBOARD] Book deleted successfully');
                          fetchBooks();
                        }
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#888',
                      fontSize: '20px',
                      marginLeft: '8px',
                      cursor: 'pointer',
                      lineHeight: 1
                    }}
                    title="Delete book"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      {/* Footer - match landing page */}
      <footer className="footer">
        Designed and Built with love in Melbourne 📚 © Saturn Program Returns Pty Ltd
        <span className="footer-link">Privacy Policy</span>
        <span className="footer-link">Terms & Conditions</span>
      </footer>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @media (max-width: 600px) {
          header { flex-direction: column !important; align-items: flex-start !important; gap: 4px !important; }
          main { padding: 0 4vw !important; }
          .spinner { width: 14px !important; height: 14px !important; }
        }
      `}</style>
    </div>
  );
} 